import styled from '@emotion/styled';
import { Constants } from '@trinity/chat-components/components/Theme';

export const Root = styled('div', {
  shouldForwardProp: (prop) => prop !== 'videoMode' && prop !== 'expanded',
})<{ videoMode: boolean; expanded: boolean }>`
  display: flex;
  width: 100%;
  /* Subtract the tab bar (~45px) and segment header row (~45px) that sit above
     this component in the DOM so the container doesn't overflow the viewport. */
  ${({ videoMode, expanded }) => {
    if (!videoMode) {
      return `
        /* Text/voice mode: no avatar panel, stretch to fill the full height.
           No card background — chat sits directly on the page background. */
        align-items: stretch;
        height: calc(100vh - var(--navbar-height) - var(--dt-chrome-height, 90px));
        background: transparent;
      `;
    }
    if (expanded) {
      return `
        /* Video mode, expanded: fill full height and stretch HeyGenPanel so
           there is no white gap below the video. */
        align-items: stretch;
        height: calc(100vh - var(--navbar-height) - var(--dt-chrome-height, 90px));
        background: white;
        border-radius: 8px;
      `;
    }
    return `
      /* Video mode, normal: align-items stretch so HeyGenPanel fills the row
         height dynamically rather than being pinned to a fixed pixel value. */
      align-items: stretch;
      height: calc(100vh - var(--navbar-height) - var(--dt-chrome-height, 90px));
      background: white;
      border-radius: 8px;
    `;
  }}
  overflow: hidden;
`;

/**
 * Establishes a stacking context so that the sidebar's absolutely-positioned
 * arrow toggler which overhangs into HeyGenPanel's space and renders above
 * HeyGenPanel's background in both normal and expanded states.
 *
 * Width is pinned to the collapsed strip so that expanding the drawer does not
 * compress HeyGenPanel or ChatPanel. The inner Wrap transitions its own width
 * and visually overflows into HeyGenPanel's space.
 */
export const SidebarWrapper = styled.div`
  position: relative;
  z-index: 10;
  flex-shrink: 0;
  /* Opt back into full card height — the outer Root uses align-items: flex-start
     to let HeyGenPanel stay at its fixed size, but the sidebar should still
     fill the available height alongside the chat column. */
  align-self: stretch;
  width: ${Constants.Sidebar.Width}px;
  overflow: visible;

  /* Hide the sidebar expand/collapse arrow toggler on the Digital Twin tab.
     Targets the StyledToggler Emotion class without touching shared components. */
  [class*='StyledToggler'] {
    display: none;
  }

  /* Hide the chat history count badge (e.g. "20") on the Digital Twin tab. */
  [class*='CountWrapper'] {
    display: none;
  }
`;

export const GalleryPanel = styled.div`
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  align-self: stretch;
`;

export const ChatPanel = styled('div', {
  shouldForwardProp: (prop) => prop !== 'expanded',
})<{ expanded: boolean }>`
  flex-grow: ${({ expanded }) => (expanded ? 0 : 1)};
  flex-shrink: 1;
  flex-basis: 0%;
  /* Opt back into full card height even though the outer Root uses
     align-items: flex-start to isolate HeyGenPanel's fixed height. */
  align-self: stretch;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  opacity: ${({ expanded }) => (expanded ? 0 : 1)};
  pointer-events: ${({ expanded }) => (expanded ? 'none' : 'auto')};
  transition:
    flex-grow 0.3s ease,
    opacity 0.25s ease;
`;
